import axios, { type AxiosInstance } from 'axios';
import { type DocGenReq } from './types';
import { type GqlDocSystemTagKind } from '../../graphql/base-types.graphql';
import { type DocGenRelation2 } from '../doc-gen';

export class ApiClient {
  constructor({ clientApiUrl, jwt }: { clientApiUrl: string; jwt?: string }) {
    this.axios = axios.create({
      baseURL: clientApiUrl,
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    });
  }

  axios: AxiosInstance;

  async getCapTableExcelExportUrl(companyId: string, capTableId: string): Promise<string> {
    return this.axios
      .get<Blob>(`/excel/cap-tables/${companyId}/${capTableId}`, {
        responseType: 'blob',
      })
      .then(res => window.URL.createObjectURL(res.data));
  }

  async getOptionProgramExcelExportUrl(companyId: string): Promise<string> {
    return this.axios
      .get<Blob>(`/excel/options/${companyId}`, {
        responseType: 'blob',
      })
      .then(res => window.URL.createObjectURL(res.data));
  }

  async getTransactionsReportExcelExportUrl(companyId: string, showShareRange: boolean): Promise<string> {
    const res = await this.axios.get<Blob>(`/excel/share-transactions/${companyId}?showShareRange=${showShareRange}`, {
      responseType: 'blob',
    });
    return window.URL.createObjectURL(res.data);
  }

  async setHubspotTag(email: string, tags: string[]): Promise<{ ok: boolean }> {
    return this.axios.post<{ ok: boolean }>('/api/resource/startupdocs/set-hubspot-tag', { email, tags }).then(res => {
      return res.data;
    });
  }
  async setMailchimpTag(email: string, tags: string[]): Promise<{ ok: boolean }> {
    return this.axios
      .post<{ ok: boolean }>('/api/resource/startupdocs/set-mailchimp-tag', { email, tags })
      .then(res => {
        return res.data;
      });
  }

  async setHubSpotUpgradePageVisit(userId: string): Promise<{ ok: boolean }> {
    return this.axios.post('/api/resource/hubspot/set-upgrade-page-visit', { userId });
  }

  async preview2(docGenReq: DocGenReq): Promise<Blob> {
    const response = await this.axios.post<Blob>('documents/preview', docGenReq, {
      responseType: 'blob',
    });
    return response.data;
  }

  async previewOptionsShareSubscriptionList(companyId: string, optionsSharesSubscriberId: string): Promise<Blob> {
    const response = await this.axios.get<Blob>(
      `/documents/preview/options-share-subscription-list/${companyId}/${optionsSharesSubscriberId}`,
      {
        responseType: 'blob',
      },
    );
    return response.data;
  }

  async previewWarrantAgreement(
    companyId: string,
    wpId: string,
    wppId: string,
    kind: 'Employee' | 'Wise' | 'Warrant' | 'Nice',
  ): Promise<Blob> {
    const kindPrefix = kind === 'Employee' ? 'qeso' : kind.toLowerCase();
    const response = await this.axios.get<Blob>(
      `/documents/preview/${kindPrefix}-agreement/${companyId}/${wpId}/${wppId}`,
      {
        responseType: 'blob',
      },
    );
    return response.data;
  }

  async previewShareSubscriptionList(companyId: string, shareIssueParticipantId: string): Promise<Blob> {
    const response = await this.axios.get<Blob>(
      `/documents/preview/share-subscription-list/${companyId}/${shareIssueParticipantId}`,
      {
        responseType: 'blob',
      },
    );
    return response.data;
  }

  async previewMeetingSummons(companyId: string, meetingId: string): Promise<Blob> {
    const response = await this.axios.get<Blob>(`/documents/preview/meeting-summons/${companyId}/${meetingId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async previewMeetingMinutes(companyId: string, meetingId: string): Promise<Blob> {
    const response = await this.axios.get<Blob>(`/documents/preview/meeting-minutes/${companyId}/${meetingId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async previewShareIssueRegistrationForm(companyId: string, shareIssueRegistrationId: string): Promise<Blob> {
    const resp = await this.axios.get<Blob>(
      `/documents/preview/si-registration-form/${companyId}/${shareIssueRegistrationId}`,
      { responseType: 'blob' },
    );
    return resp.data;
  }

  async previewWarrantRegistrationForm(companyId: string, optionsRegistrationId: string): Promise<Blob> {
    const resp = await this.axios.get<Blob>(
      `/documents/preview/warrant-registration-form/${companyId}/${optionsRegistrationId}`,
      { responseType: 'blob' },
    );
    return resp.data;
  }

  async previewWarrantSharesRegistrationForm(companyId: string, optionsSharesRegistrationId: string): Promise<Blob> {
    const resp = await this.axios.get<Blob>(
      `/documents/preview/warrant-shares-registration-form/${companyId}/${optionsSharesRegistrationId}`,
      { responseType: 'blob' },
    );
    return resp.data;
  }

  async previewConvertiblesRegistrationForm(companyId: string, optionsRegistrationId: string): Promise<Blob> {
    const resp = await this.axios.get<Blob>(
      `/documents/preview/convertible-registration-form/${companyId}/${optionsRegistrationId}`,
      { responseType: 'blob' },
    );
    return resp.data;
  }

  async previewAoaRegistrationForm(companyId: string, aoaId: string): Promise<Blob> {
    const resp = await this.axios.get<Blob>(`/documents/preview/aoa-registration-form/${companyId}/${aoaId}`, {
      responseType: 'blob',
    });
    return resp.data;
  }

  async previewAgRegistrationForm(companyId: string, agId: string): Promise<Blob> {
    const resp = await this.axios.get<Blob>(`/documents/preview/ag-registration-form/${companyId}/${agId}`, {
      responseType: 'blob',
    });
    return resp.data;
  }

  async uploadFile(
    file: File,
    fileName: string,
    companyId: string,
    relation: DocGenRelation2 | null,
    signDigitally: boolean,
    kind: GqlDocSystemTagKind,
    parentId?: string | null,
  ) {
    const body = new FormData();
    if (relation) {
      body.append('relation', JSON.stringify(relation));
    }
    body.append('kind', kind);
    if (parentId) {
      body.append('parentId', parentId);
    }
    body.append('file', file, fileName);
    body.append('signDigitally', `${signDigitally}`);
    return this.axios.post<{ uploadedDocumentId: string; affectedDocumentIds?: string[] }>(
      `/api/resource/documents/${companyId}/upload`,
      body,
    );
  }
}
